import React, { useRef } from "react";

import { Box } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import Highcharts from "highcharts";
import HighchartsReact, { HighchartsReactRefObject } from "highcharts-react-official";
import { IPortfolioConstructionPieChart } from "store/types";

interface PieChartProps {
  data: IPortfolioConstructionPieChart;
  loading: boolean;
}

export const PieChart: React.FC<PieChartProps> = ({ data, loading }) => {
  const chartRef = useRef<HighchartsReactRefObject>(null);
  const defaultOptions: Highcharts.Options = {
    chart: {
      type: "pie",
      height: 205,
      marginTop: 30,
      alignTicks: false,
      borderRadius: 16,
      events: {
        load: function () {
          this.series[0].update({
            type: "pie",
            center: ["20%", "50%"]
          });
        }
      }
    },
    title: {
      text: data.title,
      align: "left",
      style: {
        fontWeight: "500",
        fontSize: "18px",
        fontFamily: "Roboto",
        color: "#071F35"
      }
    },
    tooltip: {
      headerFormat: "",
      pointFormat: "{point.name}: <b>${point.y:,.2f}</b>"
    },
    series: [
      {
        type: "pie",
        data: data.data.data,
        dataLabels: {
          enabled: false
        },
        showInLegend: true,
        size: 112,
        dataGrouping: {
          enabled: true,
          approximation: "average",
          groupPixelWidth: 10
        }
      }
    ],
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer"
      }
    },
    credits: {
      enabled: false
    },
    legend: {
      align: "right",
      verticalAlign: "middle",
      layout: "vertical",
      x: 0,
      itemMarginTop: 5,
      itemMarginBottom: 5,
      symbolPadding: 10,
      itemStyle: {
        lineHeight: "16px"
      },
      labelFormatter: function () {
        return `<div style="display: flex; align-items: center; justify-content: space-between; min-width: 200px; width: 300px; max-width: 450px; font-size: 16px; font-weight: 400; font-family: Roboto;">
          <span>${this.name}</span>
          <span>${(this as any).percentage.toFixed(2)}%</span>
        </div>`;
      },
      useHTML: true
    },
    colors: ["#071F35", "#26A6AE", "#C09560", "#92A7BC"],
    exporting: {
      enabled: false
    },
    navigation: {
      buttonOptions: {
        enabled: false
      }
    }
  };

  const emptyStateOptions: Highcharts.Options = {
    ...defaultOptions,
    title: {
      text: data?.title || "Portfolio Allocation",
      align: "left",
      style: {
        fontWeight: "500",
        fontSize: "18px",
        fontFamily: "Roboto",
        color: "#071F35"
      }
    },
    series: [
      {
        type: "pie",
        data: [
          {
            name: "Empty",
            y: 100,
            color: "#F5F6F7"
          }
        ],
        dataLabels: {
          enabled: false
        },
        showInLegend: false,
        size: 112
      }
    ],
    tooltip: {
      enabled: false
    },
    legend: {
      enabled: false
    }
  };

  if (!data?.data?.data?.length) {
    return (
      <Box
        sx={{
          height: 205,
          maxWidth: "50%",
          flexGrow: 1,
          borderRadius: "16px",
          backgroundColor: "#ffffff",
          boxShadow: "0px 1px 2px 0px #12121714, 0px 1px 4px 0px #1212171F",
          position: "relative"
        }}
      >
        <AsyncContentMui isLoading={loading}>
          <HighchartsReact
            highcharts={Highcharts}
            options={{
              ...emptyStateOptions,
              chart: {
                ...emptyStateOptions.chart,
                marginLeft: 0,
                marginRight: 0
              }
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "65%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              color: "#92A7BC",
              fontSize: "16px",
              fontFamily: "Roboto",
              width: "300px",
              whiteSpace: "wrap"
            }}
          >
            {data.title === "Target"
              ? "Add your target allocation above — the graph will appear here once you're done."
              : "There’s no actual data yet. Add your current holdings to see how your portfolio is really allocated."}
          </Box>
        </AsyncContentMui>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 205,
        maxWidth: "50%",
        flexGrow: 1,
        borderRadius: "16px",
        backgroundColor: "#ffffff",
        boxShadow: "0 1px 2px 0 #1212171a",
        "&:hover .chart-option": {
          opacity: "1 !important"
        }
      }}
    >
      <AsyncContentMui isLoading={loading}>
        <HighchartsReact
          highcharts={Highcharts}
          ref={chartRef}
          options={{
            ...defaultOptions,
            chart: {
              ...defaultOptions.chart,
              marginLeft: 0,
              marginRight: 0
            }
          }}
        />
      </AsyncContentMui>
    </Box>
  );
};
